import { convertDateToTimeZone, DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import BaseCheckbox from 'components/atoms/base-checkbox';
import { useTranslation } from 'react-i18next';
import { memo, type FC } from 'react';

import s from './styles.module.scss';
import Status from './status';
import Reading from './reading';
import Threshold from './threshold';

import type { IPatientThresholds } from 'infrastructure/interfaces';
import type {
  TReadingGroup,
  TStatuses,
  TThresholdName,
  Value,
} from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { ReadingStatus, ReadingType } from 'infrastructure/enums';

const readingGroupLabelMap: Record<TReadingGroup, string> = {
  unreviewed: 'Unactioned',
  reviewed: 'Actioned',
  erroneous: 'Erroneous',
};

interface ReadingItemProps {
  createdAt: string;
  idx: number;
  isChecked: boolean;
  timezone: string;
  globalStatus: ReadingStatus;
  values: Value;
  statuses: TStatuses;
  readingType: ReadingType;
  thresholds: IPatientThresholds;
  thresholdName?: TThresholdName | null;
  isRecovered?: boolean;
  groupType?: TReadingGroup;
  onChange: () => void;
}

const ReadingItem: FC<ReadingItemProps> = ({
  idx,
  isChecked,
  createdAt,
  timezone,
  globalStatus,
  values,
  statuses,
  readingType,
  thresholds,
  thresholdName,
  isRecovered,
  groupType,
  onChange,
}) => {
  const { t } = useTranslation();

  const date = DateFormatter({
    date: new Date(parseInt(createdAt)),
    format: DateFormats['MM-dd-yyyy hh:mm:ss a'],
  });
  const patientTime = convertDateToTimeZone(
    new Date(parseInt(createdAt)),
    timezone,
  );
  const patientDate = DateFormatter({
    date: patientTime,
    format: DateFormats['MM-dd-yyyy hh:mm:ss a'],
  });

  return (
    <div className={s['item-wrapper']}>
      <div className={s['item-header']}>
        <p>
          <span className={s['item-index']}>#{idx + 1} </span>
          <span className={s['item-date']}>{date}</span>
        </p>
        <p className={s['item-local-date']}>
          {t('labels.patientTime')}:{' '}
          <span className={s['item-date']}>{patientDate}</span>
        </p>
        {groupType && (
          <p className={s['item-reading-group']}>
            {readingGroupLabelMap[groupType]}
          </p>
        )}
      </div>
      <div className={s['item-body']}>
        <div className={s['item-body-left']}>
          <div>
            <Status
              globalStatus={globalStatus}
              thresholdName={thresholdName}
              isRecovered={isRecovered}
              readingType={readingType}
            />
          </div>
          <div>
            <Reading
              values={values}
              statuses={statuses}
              readingType={readingType}
            />
          </div>
        </div>
        <div className={s['item-body-right']}>
          <div className={s['item-body-thresholds']}>
            <Threshold
              globalStatus={globalStatus}
              readingType={readingType}
              statuses={statuses}
              values={values}
              thresholds={thresholds}
              thresholdName={thresholdName}
            />
          </div>
          <div className={s['item-body-checkbox']}>
            <BaseCheckbox
              dataCy="linked-reading-call-checkbox"
              value={isChecked}
              onChange={onChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
export default memo(ReadingItem);
